<script>
import axios from "axios";

/**
 * Statistics of the voice robot ("ask by SMS, then call"): texts, people contacted per day, YES / NO, calls,
 * transfers to a human, appointments and talk time. Data: POST /api/ia/robot/stats (communication/ai_robot_stats.py).
 */
export default {
  name: "RobotStats",
  data() {
    const today = new Date();
    return {
      loading: false,
      error: "",
      range: { from: this.iso(this.addDays(today, -6)), to: this.iso(today) },
      agentId: "",
      stats: null,
    };
  },
  computed: {
    totals() {
      return this.stats ? this.stats.totals : null;
    },
    rates() {
      return this.stats ? this.stats.rates : {};
    },
    days() {
      return this.stats ? this.stats.by_day : [];
    },
    agents() {
      return this.stats ? this.stats.by_agent : [];
    },
    maxContacted() {
      return Math.max(1, ...this.days.map((d) => d.consent_sent));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    addDays(date, n) {
      const d = new Date(date);
      d.setDate(d.getDate() + n);
      return d;
    },
    iso(date) {
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${date.getFullYear()}-${m}-${d}`;
    },
    preset(days) {
      const today = new Date();
      this.range = { from: this.iso(this.addDays(today, -(days - 1))), to: this.iso(today) };
      this.load();
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await axios.post("/api/ia/robot/stats", {
          token: localStorage.getItem("jwt"),
          from: this.range.from,
          to: this.range.to,
          agent_id: this.agentId || undefined,
        });
        this.stats = res.data;
      } catch (err) {
        this.error =
          (err.response && err.response.data && err.response.data.error) || "Could not load the statistics.";
      } finally {
        this.loading = false;
      }
    },
    pct(value) {
      return value === null || value === undefined ? "-" : `${value}%`;
    },
    duration(seconds) {
      const s = Math.round(Number(seconds) || 0);
      if (!s) return "0m";
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;
      if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
      return m ? `${m}m ${String(r).padStart(2, "0")}s` : `${r}s`;
    },
    avgCall(seconds, calls) {
      return calls ? this.duration(seconds / calls) : "-";
    },
    dayLabel(value) {
      const [y, m, d] = value.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    },
  },
};
</script>

<template>
  <div>
    <!-- Filters -->
    <div class="card">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-sm-6 col-md-3">
            <label class="form-label fw-semibold">From</label>
            <input type="date" class="form-control" v-model="range.from" />
          </div>
          <div class="col-sm-6 col-md-3">
            <label class="form-label fw-semibold">To</label>
            <input type="date" class="form-control" v-model="range.to" />
          </div>
          <div class="col-sm-6 col-md-3" v-if="agents.length">
            <label class="form-label fw-semibold">Agent</label>
            <select class="form-select" v-model="agentId" @change="load">
              <option value="">All agents</option>
              <option v-for="a in agents.filter((x) => x.agent_id)" :key="a.agent_id" :value="a.agent_id">
                {{ a.name }}
              </option>
            </select>
          </div>
          <div class="col-md-3 d-flex gap-2 flex-wrap">
            <button class="btn btn-primary" :disabled="loading" @click="load">
              <i class="ri-refresh-line align-bottom me-1"></i> Update
            </button>
            <button class="btn btn-soft-secondary" @click="preset(1)">Today</button>
            <button class="btn btn-soft-secondary" @click="preset(7)">7 days</button>
            <button class="btn btn-soft-secondary" @click="preset(30)">30 days</button>
          </div>
        </div>
        <div class="form-text fs-12 text-muted mt-2">
          Days are Central Time. Numbers are per prospect's assigned agent. Calls include Emma's conversation and,
          after a transfer, the whole call with the human agent.
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="loading && !stats" class="text-center text-muted py-5">Loading statistics...</div>

    <template v-if="totals">
      <!-- Summary cards -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Texts sent</p>
            <h3 class="mb-1">{{ totals.texts }}</h3>
            <span class="text-muted fs-12">{{ totals.consent_sent }} asking for the call</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">People contacted</p>
            <h3 class="mb-1">{{ totals.contacted }}</h3>
            <span class="text-muted fs-12">{{ totals.waiting }} still not answering</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Said YES</p>
            <h3 class="mb-1 text-success">{{ totals.yes }}</h3>
            <span class="text-muted fs-12">{{ pct(rates.yes_rate) }} of people contacted</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Said NO / STOP</p>
            <h3 class="mb-1 text-danger">{{ totals.no }}</h3>
            <span class="text-muted fs-12">{{ pct(rates.no_rate) }} of people contacted</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Calls</p>
            <h3 class="mb-1">{{ totals.calls }}</h3>
            <span class="text-muted fs-12">
              {{ totals.answered }} answered · {{ totals.voicemail }} voicemail · {{ totals.no_answer }} no answer
            </span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Transferred to an agent</p>
            <h3 class="mb-1 text-primary">{{ totals.transfers }}</h3>
            <span class="text-muted fs-12">
              {{ pct(rates.transfer_rate) }} of answered calls ·
              {{ totals.transfer_busy + totals.transfer_failed }} could not connect
            </span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Appointments</p>
            <h3 class="mb-1">{{ totals.appointments }}</h3>
            <span class="text-muted fs-12">scheduled by Emma</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card card-animate"><div class="card-body">
            <p class="text-uppercase fw-medium text-muted mb-2">Call time</p>
            <h3 class="mb-1">{{ duration(totals.total_seconds) }}</h3>
            <span class="text-muted fs-12">
              avg {{ avgCall(totals.total_seconds, totals.answered) }} per answered call ·
              Emma alone {{ duration(totals.talk_seconds) }}
            </span>
          </div></div>
        </div>
      </div>

      <!-- By day -->
      <div class="card">
        <div class="card-header"><h5 class="card-title mb-0">By day</h5></div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table align-middle table-nowrap mb-0">
              <thead class="table-light">
                <tr>
                  <th>Day</th><th class="text-end">Contacted</th><th class="text-end">Texts</th>
                  <th class="text-end">YES</th><th class="text-end">NO</th><th class="text-end">Calls</th>
                  <th class="text-end">Answered</th><th class="text-end">Transfers</th>
                  <th class="text-end">Appointments</th><th class="text-end">Call time</th><th style="width: 18%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in days" :key="d.date">
                  <td>{{ dayLabel(d.date) }}</td>
                  <td class="text-end">{{ d.contacted }}</td>
                  <td class="text-end">{{ d.texts }}</td>
                  <td class="text-end text-success">{{ d.yes }}</td>
                  <td class="text-end text-danger">{{ d.no }}</td>
                  <td class="text-end">{{ d.calls }}</td>
                  <td class="text-end">{{ d.answered }}</td>
                  <td class="text-end text-primary">{{ d.transfers }}</td>
                  <td class="text-end">{{ d.appointments }}</td>
                  <td class="text-end">{{ duration(d.total_seconds) }}</td>
                  <td>
                    <div class="progress progress-sm">
                      <div class="progress-bar bg-info" :style="{ width: (100 * d.consent_sent) / maxContacted + '%' }"></div>
                    </div>
                  </td>
                </tr>
                <tr class="fw-semibold table-active">
                  <td>Total</td>
                  <td class="text-end">{{ totals.contacted }}</td><td class="text-end">{{ totals.texts }}</td>
                  <td class="text-end">{{ totals.yes }}</td><td class="text-end">{{ totals.no }}</td>
                  <td class="text-end">{{ totals.calls }}</td><td class="text-end">{{ totals.answered }}</td>
                  <td class="text-end">{{ totals.transfers }}</td><td class="text-end">{{ totals.appointments }}</td>
                  <td class="text-end">{{ duration(totals.total_seconds) }}</td><td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="form-text fs-12 text-muted mt-2">
            "Contacted" in the total row counts each person once for the whole range.
          </div>
        </div>
      </div>

      <!-- By agent -->
      <div class="card">
        <div class="card-header"><h5 class="card-title mb-0">By agent</h5></div>
        <div class="card-body">
          <div v-if="!agents.length" class="text-muted">No robot activity in this range.</div>
          <div v-else class="table-responsive">
            <table class="table align-middle table-nowrap mb-0">
              <thead class="table-light">
                <tr>
                  <th>Agent</th><th class="text-end">Contacted</th><th class="text-end">Texts</th>
                  <th class="text-end">YES</th><th class="text-end">NO</th><th class="text-end">Calls</th>
                  <th class="text-end">Answered</th><th class="text-end">Transfers</th>
                  <th class="text-end">Not connected</th><th class="text-end">Appointments</th>
                  <th class="text-end">Call time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in agents" :key="a.agent_id">
                  <td class="fw-medium">{{ a.name }}</td>
                  <td class="text-end">{{ a.contacted }}</td>
                  <td class="text-end">{{ a.texts }}</td>
                  <td class="text-end text-success">{{ a.yes }}</td>
                  <td class="text-end text-danger">{{ a.no }}</td>
                  <td class="text-end">{{ a.calls }}</td>
                  <td class="text-end">{{ a.answered }}</td>
                  <td class="text-end text-primary">{{ a.transfers }}</td>
                  <td class="text-end" :class="{ 'text-warning': a.transfer_busy + a.transfer_failed > 0 }">
                    {{ a.transfer_busy + a.transfer_failed }}
                  </td>
                  <td class="text-end">{{ a.appointments }}</td>
                  <td class="text-end">{{ duration(a.total_seconds) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="form-text fs-12 text-muted mt-2">
            "Not connected" = the prospect asked for an agent but the transfer could not be completed (agent
            unavailable, no CallUp user, or a technical error). Those prospects need a callback.
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
